import axios from "axios";
import axiosInstance from "../../../lib/axiosInstance";
import { API_BASE } from "../../../lib/apiBase";
import { SEED_COMBOS, comboTitle } from "./catalog";

/**
 * Motor Data reads two different endpoints depending on who is asking:
 *
 *   anonymous  → GET /propulsion/public/combinations[/{slug}]
 *                published combinations only, throttle rows truncated server-side
 *   signed in  → GET /propulsion/combinations[/{id}]
 *                everything, both curves, no ceiling
 *
 * The public read must NOT go through axiosInstance: that attaches a bearer
 * token and refreshes sessions, which is wrong for an anonymous page. Plain
 * axios against API_BASE instead.
 */

const publicClient = axios.create({ baseURL: API_BASE });

const num = (v) => (v === undefined || v === null || v === "" ? null : Number(v));

/** Efficiency is derived, not stored — thrust per watt. */
const efficiency = (thrust, power) => (thrust == null || !power ? null : thrust / power);

const normalizePoint = (p) => ({
  throttle: num(p.throttle_pct),
  voltage: num(p.voltage_v),
  current: num(p.current_a),
  rpm: num(p.rpm),
  thrust: num(p.thrust_g),
  power: num(p.power_w),
  torque: num(p.torque_nm),
  temp: num(p.temp_c),
  efficiency: efficiency(num(p.thrust_g), num(p.power_w)),
});

const rows = (points) =>
  (points || []).map(normalizePoint).filter((r) => r.throttle != null).sort((a, b) => a.throttle - b.throttle);


// Vendor product titles usually repeat the brand ("AxisFlying AxisFlying C227…"),
// and every place we render one already shows the brand separately. Strip a
// leading brand, but never to nothing — some listings are only the brand name.
const stripBrand = (brand, name) => {
  const trimmed = String(name || "").trim();
  const prefix = String(brand || "").trim();
  if (!prefix) return trimmed;
  const collapsed = prefix.replace(/\s+/g, "\\s*");
  const stripped = trimmed.replace(new RegExp(`^${collapsed}\\s*[-:·]?\\s*`, "i"), "").trim();
  return stripped || trimmed;
};

const specsOf = (src) => ({
  ...(src.motor_kv ? { kv: src.motor_kv } : {}),
  ...(src.motor_max_current_a ? { "Peak current": `${src.motor_max_current_a} A` } : {}),
  ...(src.motor_resistance_ohm ? { "Internal resistance": `${(src.motor_resistance_ohm * 1000).toFixed(1)} mΩ` } : {}),
  ...(src.motor_weight_g ? { "Motor weight": `${src.motor_weight_g} g` } : {}),
});

/** Public list/detail row → page shape. */
const fromPublic = (c) => ({
  slug: c.slug,
  id: c.id,
  motorBrand: c.motor_brand || "—",
  motorName: c.motor_name || c.name || "Untitled",
  propName: c.propeller_name || "—",
  cells: c.cells_series ? `${c.cells_series}S` : null,
  specs: specsOf(c),
  claimedSource: c.claimed_source_url || null,
  verified: Boolean(c.verified),
  hasMeasured: Boolean(c.has_measured),
  hasClaimed: Boolean(c.has_claimed),
});

/** Authenticated detail → page shape (both curves, no ceiling). */
const fromAuthed = (detail) => {
  const c = detail.combination || {};
  const motor = detail.motor || {};
  const propeller = detail.propeller || {};
  return {
    slug: c.slug,
    id: c.id,
    motorBrand: motor.brand || "—",
    motorName: motor.name || c.name || "Untitled",
    propName: propeller.name || "—",
    cells: c.cells_series ? `${c.cells_series}S` : null,
    specs: specsOf({
      motor_kv: motor.kv,
      motor_max_current_a: motor.max_current_a,
      motor_resistance_ohm: motor.resistance_ohm,
      motor_weight_g: motor.weight_g,
    }),
    claimed: rows(detail.claimed_points),
    measured: rows(detail.points).length ? rows(detail.points) : null,
    claimedSource: c.claimed_source_url || null,
    claimedTemp: null,
    methodology: c.methodology || null,
    hiddenPointCount: 0,
    verified: Boolean(c.verified),
  };
};

/**
 * The public motor catalog — every motor we hold specs for, most with no bench
 * data yet. `{ motors, count, withTestData, live }`; `live` is false when the
 * API was unreachable and the seed was used.
 */
export const listMotors = async () => {
  try {
    const { data } = await publicClient.get("/propulsion/public/motors");
    const motors = data?.motors || [];
    if (motors.length) {
      return {
        motors: motors.map((m) => ({
          brand: m.brand,
          name: stripBrand(m.brand, m.name),
          kv: m.kv,
          weightG: m.weight_g,
          maxCurrentA: m.max_current_a,
          resistanceOhm: m.resistance_ohm,
          poles: m.poles,
          statorSize: m.stator_size,
          datasheetUrl: m.datasheet_url,
          // Non-null only where a claimed-vs-measured comparison exists.
          comboSlug: m.combination_slug,
        })),
        count: data.count ?? motors.length,
        withTestData: data.with_test_data ?? 0,
        live: true,
      };
    }
  } catch (error) {
    // Fall through to the seed so a shared link never renders an empty page.
  }
  const seeded = SEED_COMBOS.map((c) => ({
    brand: c.motorBrand,
    name: c.motorName,
    kv: c.specs?.kv ?? null,
    comboSlug: c.slug,
  }));
  return { motors: seeded, count: seeded.length, withTestData: seeded.length, live: false };
};

/**
 * Every published combination.
 * `{ combos, live }` — `live` is false when the seeded fallback was used.
 */
export const listCombos = async (isAuthenticated = false) => {
  try {
    if (isAuthenticated) {
      const { data } = await axiosInstance.get("/propulsion/combinations");
      const combos = (data?.combinations || []).filter((c) => c.slug).map(fromPublic);
      if (combos.length) return { combos, live: true };
    } else {
      const { data } = await publicClient.get("/propulsion/public/combinations");
      const combos = (data?.combinations || []).map(fromPublic);
      if (combos.length) return { combos, live: true };
    }
  } catch (error) {
    // Backend unreachable or nothing published yet — the seed keeps the page
    // from rendering empty to a visitor who arrived from a shared link.
  }
  return { combos: SEED_COMBOS, live: false };
};

/** One combination by slug, with whichever curves the caller is entitled to. */
export const getCombo = async (slug, isAuthenticated = false) => {
  const fromSeed = () => SEED_COMBOS.find((c) => c.slug === slug) || null;
  try {
    if (isAuthenticated) {
      const { data } = await axiosInstance.get("/propulsion/combinations");
      const match = (data?.combinations || []).find((c) => c.slug === slug);
      if (!match) return fromSeed();
      const { data: detail } = await axiosInstance.get(`/propulsion/combinations/${match.id}`);
      return fromAuthed(detail);
    }

    const { data } = await publicClient.get(`/propulsion/public/combinations/${encodeURIComponent(slug)}`);
    const measured = rows(data.measured);
    return {
      ...fromPublic(data.combination || {}),
      claimed: rows(data.claimed),
      measured: measured.length ? measured : null,
      // Server-side truth: how many rows it withheld, and whether there is a
      // methodology to unlock. The page must not guess either.
      hiddenPointCount: data.hidden_point_count || 0,
      methodologyAvailable: Boolean(data.methodology_available),
      methodology: null,
      claimedTemp: null,
    };
  } catch (error) {
    return fromSeed();
  }
};

export { comboTitle };


/**
 * Catalog motors the propulsion model can actually simulate.
 *
 * The model falls back to kv=1000 and resistance=0.05 Ω when those are absent —
 * plausible-looking numbers that would silently produce a wrong answer, which is
 * worse than not offering the motor. So a motor is only offered here when it
 * carries the fields the solver actually reads. `no_load_current_a` is NOT
 * required: vendors almost never publish it and its fallback (0 A) is a bounded,
 * well-understood approximation rather than an invented value.
 *
 * Shaped for the calculator's catalog (id/brand/name/kv/...), not the page.
 */
export const listCalculatorMotors = async () => {
  try {
    const { data } = await publicClient.get("/propulsion/public/motors");
    return (data?.motors || [])
      .filter((m) => m.kv && m.resistance_ohm && m.weight_g)
      .map((m) => ({
        id: `cat-${m.brand}-${m.name}`.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        brand: m.brand,
        name: stripBrand(m.brand, m.name),
        kv: m.kv,
        resistance_ohm: m.resistance_ohm,
        weight_g: m.weight_g,
        max_current_a: m.max_current_a ?? undefined,
        no_load_current_a: undefined,
      }));
  } catch (error) {
    return []; // caller keeps its built-in list
  }
};

/* eslint-disable no-console */
if (process.env.NODE_ENV !== "production") {
  console.assert(stripBrand("AxisFlying", "AxisFlying C227 2207") === "C227 2207", "stripBrand: leading brand");
  console.assert(stripBrand("Brother Hobby", "BrotherHobby Avenger 2207") === "Avenger 2207", "stripBrand: spacing");
  console.assert(stripBrand("T-Motor", "MN1330 KV40") === "MN1330 KV40", "stripBrand: no false strip");
  // A listing that is only the brand must not become empty.
  console.assert(stripBrand("Emax", "Emax") === "Emax", "stripBrand: must not empty the name");
}
